import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { useMemo, useRef } from 'react';

import FlatLayout from 'components/Layout/FlatLayout';
import PageDescription from 'components/PageDescription';
import ScrollToTopButton from 'components/ScrollToTopButton';

import useIntersectionObserver from 'hooks/useIntersectionObserver';

import { sendEvent } from 'lib/gtag';
import { capitalizeEveryWord } from 'lib/string';

const SmallAds = dynamic(() => import('components/ManagedLanding/SmallAds'));
const DownloadApp = dynamic(
  () => import('components/ManagedLanding/DownloadApp')
);
const OffersList = dynamic(
  () => import('components/ManagedLanding/OffersList')
);
const CouponsList = dynamic(
  () => import('components/ManagedLanding/CouponsList')
);
const OffersShowcaseCarousel = dynamic(
  () => import('components/ManagedLanding/OffersShowcaseCarousel')
);
const HighlightedArticles = dynamic(
  () => import('components/ManagedLanding/HighlightedArticles')
);
const FrequentAskedQuestions = dynamic(
  () => import('components/ManagedLanding/FrequentAskedQuestions')
);
const Navigation = dynamic(
  () => import('components/ManagedLanding/Navigation')
);

const GA_EVENT_CATEGORY = 'modular_landing';

const GA_EVENT_VIEW = {
  small_banner: 'view_banner_lp',
  offer_selections: 'view_offersasset_lp',
  coupon_pages: 'view_coupons_lp',
  download_app: 'view_appdownload_lp',
  merchandising: 'view_showcase_lp',
  article: 'view_blogspot_lp',
};
const COMPONENT_TYPES = {
  small_banner: 'small_banner',
  offer_selections: 'offer_selections',
  coupon_pages: 'coupon_pages',
  download_app: 'download_app',
  merchandising: 'merchandising',
  article: 'article',
};

const chooseElement = (type, props) => {
  switch (type) {
    case COMPONENT_TYPES.small_banner:
      return <SmallAds {...props} />;
    case COMPONENT_TYPES.offer_selections:
      return <OffersList {...props} />;
    case COMPONENT_TYPES.coupon_pages:
      return <CouponsList {...props} />;
    case COMPONENT_TYPES.download_app:
      return <DownloadApp {...props} />;
    case COMPONENT_TYPES.merchandising:
      return <OffersShowcaseCarousel {...props} />;
    case COMPONENT_TYPES.article:
      return <HighlightedArticles {...props} />;
    case COMPONENT_TYPES.offers_navigation:
      return <Navigation {...props} />;
    default:
      // TODO: other components to deal in the future
      // like: form, store, discount meter, countdown
      return null;
  }
};

const TrackedSection = ({ type, props }) => {
  const componentRef = useRef(null);
  const eventName = GA_EVENT_VIEW[type];
  const SectionElement = chooseElement(type, props);

  useIntersectionObserver({
    target: componentRef,
    threshold: 0.4,
    enabled: !!eventName,
    onIntersect: () => {
      sendEvent({
        action: eventName,
        category: GA_EVENT_CATEGORY,
        label: props.title || null,
      });
    },
  });

  return <div ref={componentRef}>{SectionElement}</div>;
};

const ModularLanding = ({ landing: { faqs, components, seoText, name } }) => {
  const componentsProps = useMemo(() => {
    const navigationTitles = components
      .filter(
        ({ type, data }) =>
          type === COMPONENT_TYPES.offer_selections && data?.anchor
      )
      .map(({ title }) => title);

    const navigationComponent = navigationTitles.length
      ? {
          id: -1,
          type: COMPONENT_TYPES.offers_navigation,
          props: {
            titles: navigationTitles,
            gaEventCategory: GA_EVENT_CATEGORY,
          },
        }
      : null;

    let isNavigationAlreadyInserted = false;

    const formattedProps = components.flatMap(({ id, type, ...props }) => {
      const component = { id, type, props: null };

      switch (type) {
        case COMPONENT_TYPES.small_banner:
          component.props = {
            banners: props.data.banners,
            gaEventCategory: GA_EVENT_CATEGORY,
          };
          if (navigationComponent && !isNavigationAlreadyInserted) {
            isNavigationAlreadyInserted = true;
            return [component, navigationComponent];
          }
          break;
        case COMPONENT_TYPES.offer_selections:
          component.props = {
            gaEventCategory: GA_EVENT_CATEGORY,
            timelineId: props.data.id,
            title: props.title,
            initialOffers: props.offers,
          };
          break;
        case COMPONENT_TYPES.download_app:
          component.props = { gaEventCategory: GA_EVENT_CATEGORY };
          break;
        case COMPONENT_TYPES.merchandising:
          component.props = {
            gaEventCategory: GA_EVENT_CATEGORY,
            offers: props.data.offers || [],
            subtitle: props.data.subtitle,
            title: props.title,
          };
          break;
        case COMPONENT_TYPES.article:
          component.props = {
            articles: props.data.articles,
            title: props.title,
            gaEventCategory: GA_EVENT_CATEGORY,
          };
          break;
        case COMPONENT_TYPES.coupon_pages:
          {
            component.props = {
              title: props.title,
              subtitle: props.data?.subtitle,
              storeSlug: props.storeSlug || null,
              gaEventCategory: GA_EVENT_CATEGORY,
            };
          }
          break;
        default:
          /**
           * TODO: other components to deal in the future
           * like: form, store, discount meter
           */
          component.props = {};
      }

      return component;
    });

    if (navigationComponent && !isNavigationAlreadyInserted) {
      formattedProps.unshift(navigationComponent);
    }

    return formattedProps;
  }, [components]);

  return (
    <>
      <FlatLayout.Margin>
        <FlatLayout.Heading>
          <NextSeo
            description={`As melhores promoções, ofertas e cupons de ${capitalizeEveryWord(name)} no Promobit! Encontre os melhores descontos com a nossa comunidade.`}
            title={`${capitalizeEveryWord(name)} é no Promobit!`}
          />
        </FlatLayout.Heading>
        <FlatLayout.Body className="mt-8 gap-9">
          {componentsProps.map(({ id, type, props }) => (
            <TrackedSection key={id} props={props} type={type} />
          ))}
          <ScrollToTopButton gaEventCategory={GA_EVENT_CATEGORY} />
          {faqs?.length > 0 && <FrequentAskedQuestions faqs={faqs} />}
          <PageDescription pageDescription={seoText} />
        </FlatLayout.Body>
      </FlatLayout.Margin>
      <FlatLayout.Footer />
    </>
  );
};

ModularLanding.propTypes = {
  landing: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    slug: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
    isFeatured: PropTypes.bool.isRequired,
    isHighlighted: PropTypes.bool.isRequired,
    seoText: PropTypes.string.isRequired,
    faqs: PropTypes.arrayOf(
      PropTypes.shape({
        question: PropTypes.string.isRequired,
        answer: PropTypes.string.isRequired,
      })
    ).isRequired,
    components: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.number.isRequired,
        type: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        position: PropTypes.number.isRequired,
        device: PropTypes.string.isRequired,
        testAb: PropTypes.oneOfType([
          PropTypes.shape({
            variant: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          }),
          PropTypes.string,
          PropTypes.number,
        ]),
        data: PropTypes.oneOfType([
          // smallBanner data
          PropTypes.shape({
            banners: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.number.isRequired,
                position: PropTypes.number.isRequired,
                description: PropTypes.string.isRequired,
                url: PropTypes.string.isRequired,
                longUrl: PropTypes.string.isRequired,
                image: PropTypes.string.isRequired,
                mobileImage: PropTypes.string.isRequired,
                tabletImage: PropTypes.string.isRequired,
                bgColor: PropTypes.array,
                isClosable: PropTypes.bool.isRequired,
                type: PropTypes.string.isRequired,
                storeSlug: PropTypes.string,
                event: PropTypes.string.isRequired,
                height: PropTypes.string,
                visibility: PropTypes.string.isRequired,
              })
            ).isRequired,
          }),
          // offerSelections data
          PropTypes.shape({
            link: PropTypes.string.isRequired,
            id: PropTypes.number.isRequired,
          }),
          // whatsapp data (empty array)
          PropTypes.array,
          // downloadApp data (null)
          PropTypes.any,
          // couponPages data
          PropTypes.shape({
            subtitle: PropTypes.string.isRequired,
            link: PropTypes.string.isRequired,
          }),
          // form data (empty array)
          PropTypes.array,
          // store data
          PropTypes.shape({
            stores: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.number.isRequired,
                domain: PropTypes.string.isRequired,
                slug: PropTypes.string.isRequired,
                image: PropTypes.string.isRequired,
                name: PropTypes.string.isRequired,
              })
            ).isRequired,
          }),
          // merchandising data
          PropTypes.shape({
            subtitle: PropTypes.string,
            offers: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.number.isRequired,
                title: PropTypes.string.isRequired,
                slug: PropTypes.string.isRequired,
                cta: PropTypes.string.isRequired,
                price: PropTypes.number.isRequired,
                oldPrice: PropTypes.number.isRequired,
                discountPercentage: PropTypes.number.isRequired,
                priceType: PropTypes.string.isRequired,
                photo: PropTypes.string.isRequired,
                coupon: PropTypes.string,
                published: PropTypes.string.isRequired,
                aliasId: PropTypes.number,
                likes: PropTypes.number.isRequired,
                clicks: PropTypes.number.isRequired,
                views: PropTypes.number.isRequired,
                comments: PropTypes.number.isRequired,
                tags: PropTypes.arrayOf(
                  PropTypes.oneOfType([
                    PropTypes.string,
                    PropTypes.shape({
                      name: PropTypes.string.isRequired,
                      type: PropTypes.string.isRequired,
                    }),
                  ])
                ),
                statusName: PropTypes.string.isRequired,
                isHighlight: PropTypes.bool.isRequired,
                authorCount: PropTypes.number.isRequired,
                authors: PropTypes.arrayOf(
                  PropTypes.shape({
                    id: PropTypes.number.isRequired,
                    name: PropTypes.string.isRequired,
                    username: PropTypes.string.isRequired,
                    photo: PropTypes.string.isRequired,
                    typeName: PropTypes.string.isRequired,
                    level: PropTypes.number.isRequired,
                    specialityName: PropTypes.string,
                    since: PropTypes.string.isRequired,
                    link: PropTypes.string.isRequired,
                    badges: PropTypes.shape({
                      made100Comments: PropTypes.bool.isRequired,
                      sent50Offers: PropTypes.bool.isRequired,
                    }).isRequired,
                  })
                ).isRequired,
                user: PropTypes.shape({
                  id: PropTypes.number.isRequired,
                  name: PropTypes.string.isRequired,
                  username: PropTypes.string.isRequired,
                  photo: PropTypes.string.isRequired,
                  typeName: PropTypes.string.isRequired,
                  level: PropTypes.number.isRequired,
                  specialityName: PropTypes.string,
                  link: PropTypes.string.isRequired,
                  since: PropTypes.string.isRequired,
                  badges: PropTypes.shape({
                    made100Comments: PropTypes.bool.isRequired,
                    sent50Offers: PropTypes.bool.isRequired,
                  }).isRequired,
                }).isRequired,
                store: PropTypes.shape({
                  id: PropTypes.number.isRequired,
                  domain: PropTypes.string.isRequired,
                  slug: PropTypes.string.isRequired,
                  image: PropTypes.string.isRequired,
                  name: PropTypes.string.isRequired,
                }).isRequired,
                category: PropTypes.shape({
                  id: PropTypes.number.isRequired,
                  name: PropTypes.string.isRequired,
                  slug: PropTypes.string.isRequired,
                }).isRequired,
                subcategory: PropTypes.shape({
                  id: PropTypes.number.isRequired,
                  name: PropTypes.string.isRequired,
                  slug: PropTypes.string.isRequired,
                }).isRequired,
                link: PropTypes.string.isRequired,
                ratings: PropTypes.shape({
                  all: PropTypes.number.isRequired,
                  good: PropTypes.number.isRequired,
                  bad: PropTypes.number.isRequired,
                  great: PropTypes.number.isRequired,
                  amazing: PropTypes.number.isRequired,
                }).isRequired,
              })
            ).isRequired,
          }),
          // article data
          PropTypes.shape({
            articles: PropTypes.arrayOf(
              PropTypes.shape({
                id: PropTypes.number.isRequired,
                description: PropTypes.string.isRequired,
                image: PropTypes.string.isRequired,
                modifiedDate: PropTypes.string.isRequired,
                published: PropTypes.string.isRequired,
                slug: PropTypes.string.isRequired,
                thumbnail: PropTypes.string.isRequired,
                title: PropTypes.string.isRequired,
                tags: PropTypes.arrayOf(PropTypes.string).isRequired,
                comments: PropTypes.number.isRequired,
                meta: PropTypes.shape({
                  title: PropTypes.string.isRequired,
                  description: PropTypes.string.isRequired,
                }).isRequired,
                user: PropTypes.shape({
                  id: PropTypes.number.isRequired,
                  name: PropTypes.string.isRequired,
                  username: PropTypes.string.isRequired,
                  photo: PropTypes.string.isRequired,
                  typeName: PropTypes.string.isRequired,
                  level: PropTypes.number.isRequired,
                  specialityName: PropTypes.string,
                  link: PropTypes.string.isRequired,
                  since: PropTypes.string.isRequired,
                }).isRequired,
                category: PropTypes.arrayOf(
                  PropTypes.shape({
                    id: PropTypes.string.isRequired,
                    name: PropTypes.string.isRequired,
                    slug: PropTypes.string.isRequired,
                  })
                ).isRequired,
              })
            ).isRequired,
          }),
          // discount meter data
          PropTypes.shape({
            discountometer: PropTypes.number.isRequired,
          }),
        ]),
      })
    ).isRequired,
  }).isRequired,
};

export default ModularLanding;
